import { Component, defineComponent, type PropType } from 'vue';
import { CheckoutSessionCart } from '@/modules/billing/models/CheckoutSessionCart';
import { StripeCheckoutSession } from '@/modules/billing/models/stripe/StripeCheckoutSession';
import { CheckoutRoute } from '@/modules/billing/constants/CheckoutRoute';
import { FinancialAdvisorProBillingPlanForm } from '@/modules/financialadvisor/models/FinancialAdvisorProBillingPlanForm';
import { PlatformProduct } from '@/modules/platformproduct/models/PlatformProduct';
import FinancialAdvisorCheckoutService from '@/modules/financialadvisor/services/FinancialAdvisorCheckoutService';
import ModalService from '@/modules/core/ui/services/ModalService';
import BillingAdvisorProChangePlanUIService from '@/modules/billing/services/BillingAdvisorProChangePlanUIService';
import UserAddAddressModal from '@/modules/user/components/stateful/UserAddAddressModal.vue';
import { HasAuthUserMixin } from '@/modules/core/app/mixins/HasAuthUserMixin';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import Logger from '@/modules/core/app/helpers/Logger';
import ToastService from '@/modules/core/ui/services/ToastService';
import Lang from '@/modules/core/app/services/Lang';

export const FinancialAdvisorProUpsellPlanMixin: Component = defineComponent({
  mixins: [HasAuthUserMixin],
  props: {
    plan: {
      type: FinancialAdvisorProBillingPlanForm as PropType<FinancialAdvisorProBillingPlanForm>,
      required: true
    },
    platformProduct: {
      type: PlatformProduct as PropType<PlatformProduct>
    }
  },
  data(): {
    isLoadingButton: boolean;
  } {
    return {
      isLoadingButton: false
    };
  },
  computed: {
    isCurrentPlan(): boolean {
      return this.platformProduct?.productKey === this.plan.productKey;
    }
  },
  methods: {
    showAddressModal(): void {
      ModalService.show({
        parent: this,
        component: UserAddAddressModal,
        props: {
          value: this.authUser.clone()
        },
        events: {
          'address-updated': this.createCheckoutSession
        }
      });
    },
    async onGetPlan(): Promise<void> {
      if (this.isLoadingButton) {
        return;
      }

      if (this.isCurrentPlan) {
        ModalService.closeAll();
        return;
      }

      if (!this.platformProduct) {
        // As we need jurisdictionId for invoice taxes later
        if (this.authUser.contact.address?.jurisdictionId) {
          await this.createCheckoutSession();
          return;
        }

        this.showAddressModal();
        return;
      }

      BillingAdvisorProChangePlanUIService.showUpgradeDowngradeModal(
        {
          parent: this
        },
        this.plan,
        this.platformProduct
      );
    },
    async createCheckoutSession(): Promise<void> {
      this.isLoadingButton = true;
      const successUrl: string = DocumentUIService.constructUrl(
        CheckoutRoute.CHECKOUT_SUCCESS.path,
        {
          session_id: '{CHECKOUT_SESSION_ID}'
        },
        false
      );

      try {
        const response: StripeCheckoutSession = await FinancialAdvisorCheckoutService.createSession(
          new CheckoutSessionCart({
            successUrl,
            productKeys: [this.plan.productKey!]
          })
        );

        DocumentUIService.openUrl(response.url, '_self');
      } catch (error) {
        Logger.error(error);
        ToastService.show(Lang.t('financialadvisor.pro-upsell-checkout-error'));
      } finally {
        this.isLoadingButton = false;
      }
    }
  }
});
